import React, { useState } from "react";
import { Plus, ShoppingBag, Package } from "lucide-react";
import { toast } from "sonner";
import { mediaUrl, money } from "@/web/useWebApi";
import { SectionWrap, WButton } from "../theme";
import { useCart } from "../CartContext";

export function ShopSection({ section, products = [], shop, preview }) {
  const d = section.data || {};
  const cols = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-2 lg:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-4" }[Number(d.columns) || 3];
  const [cat, setCat] = useState("");
  const cats = [...new Set(products.map((p) => p.category).filter(Boolean))];
  const list = (cat ? products.filter((p) => p.category === cat) : products).slice().sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
  if (!shop?.enabled) return null;
  return (
    <SectionWrap section={section}>
      {d.intro && <p className="text-base opacity-80 mb-6 max-w-2xl">{d.intro}</p>}
      {cats.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="ws-shop-categories">
          {["", ...cats].map((c) => <button key={c} type="button" onClick={() => setCat(c)} className="px-3 h-8 text-xs font-bold uppercase tracking-wider border transition-colors" style={{ borderRadius: "var(--w-radius)", borderColor: cat === c ? "var(--w-primary)" : "var(--w-border)", background: cat === c ? "var(--w-primary)" : "transparent", color: cat === c ? "var(--w-on-primary)" : "inherit" }} data-testid={`ws-shop-cat-${c || "all"}`}>{c || "Tout"}</button>)}
        </div>
      )}
      <div className={`grid gap-5 lg:gap-6 ${cols}`} data-testid="ws-shop-grid">
        {list.map((p) => <ProductCard key={p.id} product={p} prices_ttc={shop.prices_ttc} preview={preview} />)}
        {!list.length && <p className="text-sm opacity-60 col-span-full">Aucun produit disponible pour le moment.</p>}
      </div>
    </SectionWrap>
  );
}

function ProductCard({ product: p, prices_ttc, preview }) {
  const cart = useCart();
  const [opts, setOpts] = useState(() => Object.fromEntries((p.options || []).map((o) => [o.name, ""])));
  const [img, setImg] = useState(0);
  const out = p.stock != null && p.stock <= 0;
  const add = () => {
    if (preview) { toast.info("Aperçu : la commande est désactivée"); return; }
    const missing = (p.options || []).find((o) => o.values?.length && !opts[o.name]);
    if (missing) { toast.error(`Choisissez une option « ${missing.name} »`); return; }
    cart.add({ product_id: p.id, name: p.name, price: p.price, image: p.images?.[0] || "", options: Object.fromEntries(Object.entries(opts).filter(([, v]) => v)), qty: 1, stock: p.stock });
    toast.success(`${p.name} ajouté au panier`);
  };
  return (
    <article className="group flex flex-col border overflow-hidden transition-shadow hover:shadow-xl" style={{ borderColor: "var(--w-border)", background: "var(--w-card)", borderRadius: "var(--w-radius)" }} data-testid={`ws-product-${p.id}`}>
      <div className="relative aspect-square overflow-hidden" style={{ background: "var(--w-muted)" }}>
        {p.images?.length ? <img src={mediaUrl(p.images[img] || p.images[0])} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          : <div className="w-full h-full flex items-center justify-center opacity-30"><Package className="h-12 w-12" /></div>}
        {p.featured && <span className="absolute top-3 left-3 text-[9px] uppercase tracking-wider font-bold px-2 py-1" style={{ background: "var(--w-accent)", color: "var(--w-on-accent)" }}>À la une</span>}
        {out && <span className="absolute top-3 right-3 text-[9px] uppercase tracking-wider font-bold px-2 py-1 bg-[#0A0A0A] text-white">Épuisé</span>}
        {p.images?.length > 1 && <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">{p.images.slice(0, 6).map((_, i) => <button key={i} type="button" onClick={() => setImg(i)} className={`h-1.5 w-1.5 rounded-full ${i === img ? "bg-white" : "bg-white/50"}`} aria-label={`Image ${i + 1}`} />)}</div>}
      </div>
      <div className="p-4 lg:p-5 flex flex-col flex-1">
        {p.category && <div className="text-[9px] uppercase tracking-[0.25em] font-bold opacity-50">{p.category}</div>}
        <h3 className="mt-1 text-base lg:text-lg font-black tracking-tight leading-tight">{p.name}</h3>
        {p.description && <p className="mt-1.5 text-xs opacity-70 line-clamp-3 flex-1">{p.description}</p>}
        {!!(p.options || []).length && (
          <div className="mt-3 grid grid-cols-2 gap-2">
            {p.options.map((o) => (
              <select key={o.name} value={opts[o.name] || ""} onChange={(e) => setOpts({ ...opts, [o.name]: e.target.value })} className="h-9 px-2 text-xs border bg-transparent" style={{ borderColor: "var(--w-border)", borderRadius: "var(--w-radius)" }} data-testid={`ws-product-opt-${p.id}-${o.name}`}>
                <option value="">{o.name}</option>{(o.values || []).map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            ))}
          </div>
        )}
        <div className="mt-4 flex items-center justify-between gap-3">
          <div>
            <div className="text-xl font-black" style={{ color: "var(--w-primary)" }} data-testid={`ws-product-price-${p.id}`}>{money(p.price)}{p.compare_price > p.price && <span className="ml-2 text-xs line-through opacity-50 font-normal">{money(p.compare_price)}</span>}</div>
            <div className="text-[10px] opacity-50">{prices_ttc === false ? "HT" : "TTC"}{p.stock != null && p.stock > 0 && p.stock <= 5 ? ` · plus que ${p.stock}` : ""}</div>
          </div>
          <WButton size="sm" onClick={add} disabled={out} data-testid={`ws-product-add-${p.id}`}><Plus className="h-3.5 w-3.5" /> Ajouter</WButton>
        </div>
      </div>
    </article>
  );
}

export function CartButton() {
  const cart = useCart();
  return (
    <button type="button" onClick={() => cart.setOpen(true)} className="relative inline-flex items-center gap-2 h-10 px-4 text-sm font-bold transition-transform hover:-translate-y-0.5" style={{ background: "var(--w-primary)", color: "var(--w-on-primary)", borderRadius: "var(--w-radius)" }} data-testid="ws-cart-button">
      <ShoppingBag className="h-4 w-4" /> Panier
      {cart.count > 0 && <span className="absolute -top-2 -right-2 h-5 min-w-5 px-1 rounded-full text-[10px] font-black flex items-center justify-center" style={{ background: "var(--w-accent)", color: "var(--w-on-accent)" }} data-testid="ws-cart-count">{cart.count}</span>}
    </button>
  );
}
